import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forkJoin, fromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AdminConfigService } from '../../services/admin-config.service';
import { ToastNotificationComponent } from '../../toast-notification/toast-notification.component';
import { AcsUserListItem, AcsUserDetail, AcsGroupListItem, AcsDirectoryPerson, AcsLocAssignment, AcsGroupAssignment, AcsReferenceItem } from '../models/acs.models';
import { ACS_TOAST_SUCCESS_DURATION, ACS_TOAST_ERROR_DURATION } from '../utils/acs.constants';
import { extractErrorMessage, executeBulkDelete, formatBulkDeleteToast } from '../utils/acs.utils';
import { AcsBulkDeleteState } from '../utils/acs-bulk-delete-state';

@Component({
  selector: 'app-acs-user-management',
  templateUrl: './acs-user-management.component.html',
  styleUrls: ['../acs-shared.css'],
  standalone: false
})
export class AcsUserManagementComponent implements OnInit, OnDestroy {

  @ViewChild('toastNotification') toastNotification!: ToastNotificationComponent;

  users: AcsUserListItem[] = [];
  selectedUser: AcsUserListItem | null = null;
  allLocs: { locCode: string; description: string }[] = [];
  groupList: AcsGroupListItem[] = [];
  userForm!: FormGroup;
  nameFilter: string = '';
  officeFilter: string = '';
  isEditing: boolean = false;
  showForm: boolean = false;

  hasSearched: boolean = false;

  bulkDelete = new AcsBulkDeleteState<AcsUserListItem>(u => u.userId, () => this.users);

  roles: { value: string; text: string }[] = [];
  accessLevels: { value: string; text: string }[] = [];

  assignedLocs: { locCode: string; description: string }[] = [];
  assignedGroups: { groupId: number; groupCode: string; groupName: string }[] = [];

  locSearchTerm: string = '';
  groupSearchTerm: string = '';
  showLocDropdown: boolean = false;
  showGroupDropdown: boolean = false;

  selectedAssignedLocs: Set<string> = new Set();
  showRemoveLocConfirm: boolean = false;
  selectedAssignedGroups: Set<number> = new Set();
  showRemoveGroupConfirm: boolean = false;

  showPeopleSearch: boolean = false;
  peopleSearchTerm: string = '';
  peopleResults: AcsDirectoryPerson[] = [];
  peopleSearching: boolean = false;
  peopleSearched: boolean = false;
  private _allUserEmails: Set<string> = new Set();

  pendingLocSelections: Set<string> = new Set();

  private _saveLocTrigger$ = new Subject<void>();
  private _saveAssignmentsTrigger$ = new Subject<void>();
  private _destroy$ = new Subject<void>();

  constructor(
    private _adminConfigService: AdminConfigService,
    private _fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.createForm();
    this._adminConfigService.getReferenceData().subscribe({
      next: (data) => {
        this.roles = data.roles.map((r: AcsReferenceItem) => ({ value: r.id.toString(), text: r.name }));
        this.accessLevels = data.accessLevels.map((a: AcsReferenceItem) => ({ value: a.id.toString(), text: a.name }));
      }
    });
    this.loadAllUserEmails();
    this._adminConfigService.getAllActiveLocs().subscribe({
      next: (data) => this.allLocs = data.map(l => ({ locCode: l.locCode, description: l.description || '' }))
    });
    this._adminConfigService.getGroups().subscribe({
      next: (data) => this.groupList = data
    });

    this._saveLocTrigger$.pipe(debounceTime(500), takeUntil(this._destroy$)).subscribe(() => this.persistLocAssignments());
    this._saveAssignmentsTrigger$.pipe(debounceTime(500), takeUntil(this._destroy$)).subscribe(() => this.persistAssignments());
    fromEvent(document, 'click').pipe(takeUntil(this._destroy$)).subscribe(() => this.closeAllAssignDropdowns());
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  createForm(): void {
    this.userForm = this._fb.group({
      userId: [0],
      userEmail: ['', Validators.required],
      userName: ['', Validators.required],
      userRole: ['', Validators.required],
      accessLevel: ['10', Validators.required],
      phoneNumber: [''],
      faxNumber: [''],
      officeLocation: [''],
      defaultLocCode: [''],
      defaultGroupId: [null],
      inactiveDate: [null],
      useDefaultFilters: [false]
    });
  }

  loadUsers(): void {
    if (!this.nameFilter.trim() && !this.officeFilter.trim()) {
      this.users = [];
      this.hasSearched = false;
      return;
    }
    this.bulkDelete.selectedIds = new Set();
    this.hasSearched = true;
    this._adminConfigService.getUsers(this.nameFilter, this.officeFilter).subscribe({
      next: (data) => this.users = data
    });
  }

  selectUser(user: AcsUserListItem): void {
    this.selectedUser = user;
    this.isEditing = true;
    this.showForm = true;
    this.selectedAssignedLocs = new Set();
    this.selectedAssignedGroups = new Set();

    this._adminConfigService.getUserById(user.userId).subscribe({
      next: (detail: AcsUserDetail) => {
        this.userForm.patchValue({
          userId: detail.userId,
          userEmail: detail.userEmail,
          userName: detail.userName,
          userRole: detail.userRole,
          accessLevel: detail.accessLevel,
          phoneNumber: detail.phoneNumber,
          faxNumber: detail.faxNumber,
          officeLocation: detail.officeLocation,
          defaultLocCode: detail.defaultLocCode,
          defaultGroupId: detail.defaultGroupId,
          inactiveDate: detail.inactiveDate ? detail.inactiveDate.substring(0, 10) : null,
          useDefaultFilters: detail.useDefaultFilters
        });
        this.assignedLocs = (detail.assignedLocs || []).map((l: AcsLocAssignment) => ({
          locCode: l.locCode,
          description: l.description || ''
        })).sort((a, b) => a.locCode.localeCompare(b.locCode));
        this.assignedGroups = (detail.assignedGroups || []).map((g: AcsGroupAssignment) => ({
          groupId: g.groupId,
          groupCode: g.groupCode || '',
          groupName: g.groupName || ''
        })).sort((a, b) => a.groupCode.localeCompare(b.groupCode));
      }
    });
  }

  addNewUser(): void {
    this.showPeopleSearch = true;
    this.peopleSearchTerm = '';
    this.peopleResults = [];
    this.peopleSearched = false;
  }

  private loadAllUserEmails(): void {
    this._adminConfigService.getUsers().subscribe({
      next: (all: AcsUserListItem[]) => {
        this._allUserEmails = new Set(all.map(u => u.userEmail?.toLowerCase()));
      }
    });
  }

  searchAD(): void {
    if (!this.peopleSearchTerm.trim()) return;
    this.peopleSearching = true;
    this._adminConfigService.searchDirectory(this.peopleSearchTerm).subscribe({
      next: (results: AcsDirectoryPerson[]) => {
        this.peopleResults = results;
        this.peopleSearching = false;
        this.peopleSearched = true;
      },
      error: () => {
        this.toastNotification.showToast('Failed to search directory', ACS_TOAST_ERROR_DURATION, 'error');
        this.peopleSearching = false;
      }
    });
  }

  selectPerson(person: AcsDirectoryPerson): void {
    this.showPeopleSearch = false;
    this.selectedUser = null;
    this.isEditing = false;
    this.showForm = true;
    this.userForm.reset({ userId: 0, accessLevel: '10', useDefaultFilters: false });
    this.assignedLocs = [];
    this.assignedGroups = [];
    this.userForm.patchValue({
      userName: person.displayName,
      userEmail: person.email,
      phoneNumber: person.mainPhone || person.mobilePhone,
      officeLocation: person.officeLocation
    });
  }

  isExistingUser(email: string): boolean {
    return this._allUserEmails.has(email?.toLowerCase());
  }

  saveUser(): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }
    const formValue = this.userForm.value;
    this._adminConfigService.saveUser(formValue).subscribe({
      next: (result) => {
        if (result) {
          this.toastNotification.showToast('User saved successfully', ACS_TOAST_SUCCESS_DURATION, 'success');
          const email: string = formValue.userEmail?.toLowerCase();
          if (email) this._allUserEmails.add(email);
          this.loadUsers();
          this.showForm = false;
        }
      },
      error: (err) => {
        const msg: string = extractErrorMessage(err, 'Failed to save user');
        this.toastNotification.showToast(msg, ACS_TOAST_ERROR_DURATION, 'error');
      }
    });
  }

  get defaultLocOptions(): { locCode: string; description: string }[] {
    const currentDefault = this.userForm.get('defaultLocCode')?.value;
    if (currentDefault && !this.assignedLocs.some(l => l.locCode === currentDefault)) {
      const desc = this.allLocs.find(l => l.locCode === currentDefault)?.description || '';
      return [{ locCode: currentDefault, description: desc }, ...this.assignedLocs];
    }
    return this.assignedLocs;
  }

  // --- Trade Unit Assignment Methods ---

  get filteredLocs(): { locCode: string; description: string }[] {
    const assignedSet = new Set(this.assignedLocs.map(l => l.locCode));
    let items = this.allLocs.filter(l => !assignedSet.has(l.locCode));
    if (this.locSearchTerm) {
      const term = this.locSearchTerm.toLowerCase();
      items = items.filter(l =>
        l.locCode?.toLowerCase().includes(term) || l.description?.toLowerCase().includes(term)
      );
    }
    return items;
  }

  togglePendingLoc(loc: { locCode: string; description: string }): void {
    if (this.pendingLocSelections.has(loc.locCode)) {
      this.pendingLocSelections.delete(loc.locCode);
    } else {
      this.pendingLocSelections.add(loc.locCode);
    }
    this.pendingLocSelections = new Set(this.pendingLocSelections);
  }

  isPendingLoc(locCode: string): boolean { return this.pendingLocSelections.has(locCode); }
  get pendingLocCount(): number { return this.pendingLocSelections.size; }

  addPendingLocs(): void {
    for (const locCode of this.pendingLocSelections) {
      if (this.assignedLocs.some(l => l.locCode === locCode)) continue;
      const loc = this.allLocs.find(l => l.locCode === locCode);
      if (loc) this.assignedLocs.push({ locCode: loc.locCode, description: loc.description });
    }
    this.assignedLocs.sort((a, b) => a.locCode.localeCompare(b.locCode));
    this.pendingLocSelections = new Set();
    this.selectedAssignedLocs = new Set();
    this.locSearchTerm = '';
    this.showLocDropdown = false;
    this._saveLocTrigger$.next();
  }

  closeAllAssignDropdowns(): void {
    this.showLocDropdown = false;
    this.showGroupDropdown = false;
    this.pendingLocSelections = new Set();
    this.pendingGroupSelections = new Set();
  }

  openLocDropdown(): void {
    this.showLocDropdown = true;
    this.showGroupDropdown = false;
    this.pendingGroupSelections = new Set();
  }

  onLocSearchBlur(): void {
    setTimeout(() => {
      if (this.pendingLocSelections.size === 0) this.showLocDropdown = false;
    }, 150);
  }

  private persistLocAssignments(): void {
    if (!this.selectedUser) return;
    const locCodes = this.assignedLocs.map(l => l.locCode);
    this._adminConfigService.saveUserLocs(this.selectedUser.userId, locCodes).subscribe({
      error: (err) => {
        const msg: string = extractErrorMessage(err, 'Failed to save trade unit assignments');
        this.toastNotification.showToast(msg, ACS_TOAST_ERROR_DURATION, 'error');
      }
    });
  }

  // --- Group Assignment Methods ---

  pendingGroupSelections: Set<number> = new Set();

  get filteredGroups(): AcsGroupListItem[] {
    const assignedSet = new Set(this.assignedGroups.map(g => g.groupId));
    let items = this.groupList.filter(g => !assignedSet.has(g.groupId));
    if (this.groupSearchTerm) {
      const term = this.groupSearchTerm.toLowerCase();
      items = items.filter(g =>
        g.groupCode?.toLowerCase().includes(term) || g.groupName?.toLowerCase().includes(term)
      );
    }
    return items;
  }

  togglePendingGroup(group: AcsGroupListItem): void {
    if (this.pendingGroupSelections.has(group.groupId)) {
      this.pendingGroupSelections.delete(group.groupId);
    } else {
      this.pendingGroupSelections.add(group.groupId);
    }
    this.pendingGroupSelections = new Set(this.pendingGroupSelections);
  }

  isPendingGroup(groupId: number): boolean { return this.pendingGroupSelections.has(groupId); }
  get pendingGroupCount(): number { return this.pendingGroupSelections.size; }

  addPendingGroups(): void {
    let addedLocCount = 0;
    for (const groupId of this.pendingGroupSelections) {
      const group = this.groupList.find(g => g.groupId === groupId);
      if (!group || this.assignedGroups.some(g => g.groupId === groupId)) continue;
      this.assignedGroups.push({ groupId: group.groupId, groupCode: group.groupCode, groupName: group.groupName });
      const groupLocs: AcsLocAssignment[] = group.groupLocs || [];
      for (const gl of groupLocs) {
        if (!this.assignedLocs.some(l => l.locCode === gl.locCode)) {
          this.assignedLocs.push({ locCode: gl.locCode, description: gl.description || '' });
          addedLocCount++;
        }
      }
    }
    this.assignedGroups.sort((a, b) => a.groupCode.localeCompare(b.groupCode));
    if (addedLocCount > 0) this.assignedLocs.sort((a, b) => a.locCode.localeCompare(b.locCode));
    if (addedLocCount > 0) {
      this.toastNotification.showToast(`Added ${addedLocCount} trade unit(s) from selected groups`, ACS_TOAST_SUCCESS_DURATION, 'success');
    }
    this.pendingGroupSelections = new Set();
    this.selectedAssignedGroups = new Set();
    this.groupSearchTerm = '';
    this.showGroupDropdown = false;
    this._saveAssignmentsTrigger$.next();
  }

  openGroupDropdown(): void {
    this.showGroupDropdown = true;
    this.showLocDropdown = false;
    this.pendingLocSelections = new Set();
  }

  onGroupSearchBlur(): void {
    setTimeout(() => {
      if (this.pendingGroupSelections.size === 0) this.showGroupDropdown = false;
    }, 150);
  }

  private persistAssignments(): void {
    if (!this.selectedUser) return;
    const locCodes = this.assignedLocs.map(l => l.locCode);
    const groupIds = this.assignedGroups.map(g => g.groupId);
    forkJoin([
      this._adminConfigService.saveUserLocs(this.selectedUser.userId, locCodes),
      this._adminConfigService.saveUserGroups(this.selectedUser.userId, groupIds)
    ]).subscribe({
      error: (err) => {
        const msg: string = extractErrorMessage(err, 'Failed to save assignments');
        this.toastNotification.showToast(msg, ACS_TOAST_ERROR_DURATION, 'error');
      }
    });
  }

  // --- Assigned Loc Selection ---

  get selectedAssignedLocCount(): number { return this.selectedAssignedLocs.size; }
  isAssignedLocSelected(locCode: string): boolean { return this.selectedAssignedLocs.has(locCode); }

  toggleAssignedLocSelect(locCode: string): void {
    if (this.selectedAssignedLocs.has(locCode)) { this.selectedAssignedLocs.delete(locCode); }
    else { this.selectedAssignedLocs.add(locCode); }
    this.selectedAssignedLocs = new Set(this.selectedAssignedLocs);
  }

  get allAssignedLocsSelected(): boolean {
    return this.assignedLocs.length > 0 && this.assignedLocs.every(l => this.selectedAssignedLocs.has(l.locCode));
  }

  toggleSelectAllAssignedLocs(): void {
    if (this.allAssignedLocsSelected) {
      for (const l of this.assignedLocs) { this.selectedAssignedLocs.delete(l.locCode); }
    } else {
      for (const l of this.assignedLocs) { this.selectedAssignedLocs.add(l.locCode); }
    }
    this.selectedAssignedLocs = new Set(this.selectedAssignedLocs);
  }

  requestRemoveAssignedLocs(): void { this.showRemoveLocConfirm = true; }
  cancelRemoveAssignedLocs(): void { this.showRemoveLocConfirm = false; }

  confirmRemoveAssignedLocs(): void {
    this.assignedLocs = this.assignedLocs.filter(l => !this.selectedAssignedLocs.has(l.locCode));
    this.selectedAssignedLocs = new Set();
    this.showRemoveLocConfirm = false;
    this._saveLocTrigger$.next();
  }

  // --- Assigned Group Selection ---

  get selectedAssignedGroupCount(): number { return this.selectedAssignedGroups.size; }
  isAssignedGroupSelected(groupId: number): boolean { return this.selectedAssignedGroups.has(groupId); }

  toggleAssignedGroupSelect(groupId: number): void {
    if (this.selectedAssignedGroups.has(groupId)) { this.selectedAssignedGroups.delete(groupId); }
    else { this.selectedAssignedGroups.add(groupId); }
    this.selectedAssignedGroups = new Set(this.selectedAssignedGroups);
  }

  get allAssignedGroupsSelected(): boolean {
    return this.assignedGroups.length > 0 && this.assignedGroups.every(g => this.selectedAssignedGroups.has(g.groupId));
  }

  toggleSelectAllAssignedGroups(): void {
    if (this.allAssignedGroupsSelected) {
      for (const g of this.assignedGroups) { this.selectedAssignedGroups.delete(g.groupId); }
    } else {
      for (const g of this.assignedGroups) { this.selectedAssignedGroups.add(g.groupId); }
    }
    this.selectedAssignedGroups = new Set(this.selectedAssignedGroups);
  }

  requestRemoveAssignedGroups(): void { this.showRemoveGroupConfirm = true; }
  cancelRemoveAssignedGroups(): void { this.showRemoveGroupConfirm = false; }

  confirmRemoveAssignedGroups(): void {
    const removingIds: Set<number> = this.selectedAssignedGroups;
    this.assignedGroups = this.assignedGroups.filter(g => !removingIds.has(g.groupId));

    const remainingGroupLocCodes: Set<string> = new Set();
    for (const ag of this.assignedGroups) {
      const g: AcsGroupListItem | undefined = this.groupList.find(gl => gl.groupId === ag.groupId);
      if (g?.groupLocs) {
        for (const gl of g.groupLocs) { remainingGroupLocCodes.add(gl.locCode); }
      }
    }

    const orphanedLocCodes: Set<string> = new Set();
    for (const id of removingIds) {
      const group: AcsGroupListItem | undefined = this.groupList.find(g => g.groupId === id);
      if (group?.groupLocs) {
        for (const gl of group.groupLocs) {
          if (!remainingGroupLocCodes.has(gl.locCode)) { orphanedLocCodes.add(gl.locCode); }
        }
      }
    }

    if (orphanedLocCodes.size > 0) {
      this.assignedLocs = this.assignedLocs.filter(l => !orphanedLocCodes.has(l.locCode));
    }

    this.selectedAssignedGroups = new Set();
    this.showRemoveGroupConfirm = false;
    this._saveAssignmentsTrigger$.next();
  }

  // --- Utility Methods ---

  getUserId(email: string): string {
    return email?.split('@')[0] || '';
  }

  isActive(user: AcsUserListItem): boolean {
    return !user.inactiveDate || new Date(user.inactiveDate) > new Date();
  }

  cancel(): void {
    this.showForm = false;
    this.selectedUser = null;
  }

  // --- Bulk Delete ---

  confirmBulkDelete(): void {
    this.bulkDelete.isDeleting = true;
    const ids: number[] = [...this.bulkDelete.selectedIds];
    const deletedUsers: AcsUserListItem[] = this.users.filter(u => ids.includes(u.userId));
    executeBulkDelete(ids, (id: number) => this._adminConfigService.deleteUser(id))
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (result) => {
          const toast = formatBulkDeleteToast(result, 'user');
          this.toastNotification.showToast(toast.message, toast.type === 'success' ? ACS_TOAST_SUCCESS_DURATION : ACS_TOAST_ERROR_DURATION, toast.type);
          for (const u of deletedUsers) {
            const email: string = u.userEmail?.toLowerCase();
            if (email) this._allUserEmails.delete(email);
          }
          if (this.selectedUser && ids.includes(this.selectedUser.userId)) {
            this.showForm = false;
            this.selectedUser = null;
          }
          this.bulkDelete.reset();
          this.loadUsers();
        }
      });
  }
}
